import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { RouterSelectors } from '@next/core-lib/router-store';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { combineLatest, of, EMPTY, forkJoin } from 'rxjs';
import {
  switchMap,
  take,
  startWith,
  catchError,
  map,
  concatMap,
  withLatestFrom,
  filter,
} from 'rxjs/operators';

import * as actions from './actions';
import {
  selectProfileId,
  selectApplicationsForGroup,
  selectUnselectedApplicationGroups,
  selectAccessRulesForGroup,
} from './selectors';
import { AccessRule, Profile, AppState } from '../shared/models';
import { ApiService } from '../shared/services/api.service';

@Injectable()
export class Effects {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly api = inject(ApiService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  initCore$ = createEffect(() =>
    forkJoin(
      this.actions$.pipe(ofType(UserActions.userInitialized), take(1)),
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(({ payload }: RouterNavigatedAction) => payload.event.url.indexOf('/auth') === -1),
        take(1),
      ),
    ).pipe(
      withLatestFrom(this.store.pipe(select(RouterSelectors.getQueryParams))),
      map(([_, params]) => {
        const hasParams = params.ExternalId && params.System && params.ExternalEntityTypeEnum;
        if (!hasParams) {
          return CoreActions.fatalError({
            message: this.locale.translate('required_parameters_missing'),
          });
        } else {
          return actions.init({
            externalId: params.ExternalId,
            system: params.System,
            externalEntityTypeEnum: params.ExternalEntityTypeEnum,
          });
        }
      }),
    ),
  );

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.init),
      switchMap(({ externalId, system, externalEntityTypeEnum }) =>
        combineLatest([
          this.api.getApplications(),
          this.api.getApplicationGroups(),
          this.api.getRoles(),
          this.api
            .getProfileId(externalId, system, externalEntityTypeEnum)
            .pipe(
              switchMap((profile) =>
                this.api
                  .getUseraccess(profile.profileId)
                  .pipe(map((accessRules) => [profile, accessRules] as [Profile, AccessRule[]])),
              ),
            ),
        ]).pipe(
          concatMap(([applications, applicationGroups, roles, [profile, accessRules]]) => [
            actions.setApplications({ applications }),
            actions.setApplicationGroups({ applicationGroups }),
            actions.setRoles({ roles }),
            actions.setAccessRules({ accessRules }),
            actions.setExternalUser({ externalId, system, externalEntityTypeEnum }),
            actions.setProfileId({ profileId: profile.profileId }),
            CoreActions.setLoading({ loading: false }),
            actions.initialized(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          catchError((error: Error | HttpErrorResponse) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              return of(
                CoreActions.setLoading({ loading: false }),
                CoreActions.fatalError({
                  message: this.locale.translate('contactperson_employee_not_found'),
                  code: 404,
                }),
              );
            } else if (
              error instanceof HttpErrorResponse &&
              error.status === 500 &&
              typeof error.error?.reason === 'string'
            ) {
              return of(
                CoreActions.setLoading({ loading: false }),
                CoreActions.fatalError({ message: error.error.reason }),
              );
            } else {
              return of(
                CoreActions.setLoading({ loading: false }),
                CoreActions.fatalError({ message: error.message }),
              );
            }
          }),
        ),
      ),
    ),
  );

  selectApplicationGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.selectApplicationGroups),
      withLatestFrom(this.store.pipe(select(selectUnselectedApplicationGroups))),
      switchMap(([_, unselectedApplicationGroups]) =>
        this.dialog.multiselect({
          title: this.locale.translate('add_applicationgroup'),
          selected: [],
          emptyText: this.locale.translate('no_applicationgroups_to_add'),
          options: unselectedApplicationGroups.map((applicationGroup) => ({
            value: applicationGroup,
            name: applicationGroup.displayName,
          })),
        }),
      ),
      concatMap(({ status, result }) =>
        status === DIALOG_RETURN_TYPES.confirm
          ? result.map((applicationGroup) =>
              actions.setGroupUseraccess({ groupId: applicationGroup.id }),
            )
          : EMPTY,
      ),
    ),
  );

  setUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setUseraccess),
      withLatestFrom(this.store.pipe(select(selectProfileId))),
      switchMap(([action, profileId]) =>
        this.api.setUseraccess(profileId, action.applicationKey, action.roleKey).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: this.locale.translate('saved_useraccess_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  setGroupUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setGroupUseraccess),
      withLatestFrom(this.store.pipe(select(selectProfileId))),
      concatMap(([action, profileId]) =>
        this.store.pipe(
          select(selectApplicationsForGroup(action.groupId)),
          take(1),
          switchMap((applications) =>
            combineLatest(
              applications.map((application) =>
                this.api.setUseraccess(profileId, application.key, application.defaultRoleKey),
              ) as [],
            ),
          ),
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: this.locale.translate('saved_useraccess_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteGroupUseraccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteGroupUseraccess),
      switchMap((action) =>
        this.store.pipe(
          select(selectAccessRulesForGroup(action.groupId)),
          take(1),
          map((oldAccessRules) => [oldAccessRules, action.groupId]),
        ),
      ),
      withLatestFrom(this.store.pipe(select(selectProfileId))),
      switchMap(([[oldAccessRules, groupId], profileId]: [[AccessRule[], number], number]) =>
        combineLatest(
          oldAccessRules.map((accessRule) =>
            this.api.deleteUseraccess(profileId, accessRule.applicationKey),
          ),
        ).pipe(
          concatMap((_) => [
            actions.deleteGroupUseraccessSuccess({ groupId }),
            NotificationActions.successSnackbar({
              message: this.locale.translate('removed_useraccess_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );
}
